import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import MetaTags from 'react-meta-tags';
import { Form, FormikHelpers, FormikValues } from 'formik';
import { AuBreadcrumbs, IAuBreadcrumbItem } from 'components/DTA';
import { FormContext, FormContextProvider, IFormContextType } from 'contexts/FormContext';
import { FormStep, IFormAlertType } from 'types/forms/formTypes';
import { FormAlert } from 'components/CASAForm';
import { AuFormItemWrapper } from 'components/DTA';

import { ExitModal } from 'components/CASAForm/ExitModal';
import { DampReportApplicationFormData } from 'types/forms/dampReportApplicationTypes';
import DampReportDetails from './DampReportDetails';
import { dampReportValidationSchema } from './validationSchemas/ValidationSchemas';
import ReportSummaryAndDeclaration from './ReportSummaryAndDeclaration';
import { FORM_ERROR_MESSAGES } from 'internalConstants';
import FinalStep from './FinalStep';
import useDampReportService from 'services/dampReportService/useDampReportService';
import { OrganisationContext } from 'contexts';
import { Loader } from 'components/common';

const DampReportApplication: React.FC = () => {
  const damReportService = useDampReportService();
  const { organisationDetailsResponse } = useContext(OrganisationContext);
  const { handleBack, cleanup, clickListener, modal, url, setModal } = useContext(FormContext);
  const [isBackButtonClicked, setBackbuttonPress] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    window.history.pushState(window.location.search, '', window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
  }, []);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (!isBackButtonClicked) {
      setShowModal(true);
    }
  };

  const toggle = () => {
    setShowModal(!showModal);
    window.history.pushState(window.location.search, '', window.location.pathname);
    setBackbuttonPress(false);
  };

  const OK = () => {
    setShowModal(!showModal);
    setBackbuttonPress(true);
    window.history.back();
  };

  const exitModalParameters = {
    isOpen: showModal,
    toggleFunction: toggle,
    OKFunction: OK
  };
  const organisationId = organisationDetailsResponse ? organisationDetailsResponse?.value?.value[0]?.accountid : '';
  const STEPS: FormStep[] = [
    {
      id: 'reportDetails',
      render: (props) => (
        <DampReportDetails />
      ),
      validationSchema: dampReportValidationSchema
    },
    {
      id: 'summaryAndDeclaration',
      render: (props) => (
        <ReportSummaryAndDeclaration />
      ),
      validationSchema: yup.object().shape({
        declaration: yup.boolean()
          .oneOf([true], FORM_ERROR_MESSAGES.DECLARATION)
          .required(FORM_ERROR_MESSAGES.DECLARATION)
      })
    },
    {
      id: 'Final',
      render: () => (
        <FinalStep organisationId={organisationId} />
      ),
      skipNavigation: true
    }
  ];

  if (!organisationId) {
    return <Loader />;
  }
  const initialValues: DampReportApplicationFormData = {
    permissionType: '',
    dateOfTest: '',
    reasonForTest: '',
    ageRange: '',
    ssaaType: '',
    state: 'ACT',
    region: '',
    positiveTo: '',
    alcoholReading: '',
    methodOfTesting: '',
    declaration: false
  };

  const errorAlert: IFormAlertType = {
    as: 'error',
    title: 'There was an issue submitting your application',
    message: 'Please try again'
  };

  const handleSubmit = async (values: DampReportApplicationFormData, actions: FormikHelpers<DampReportApplicationFormData & FormikValues>): Promise<IFormAlertType | null> => {
    const { setSubmitting } = actions;
    const dampApplicationPayLoad = {
      version: '01',
      name: 'Damp Report',
      orgid: organisationDetailsResponse?.value?.value[0]?.accountid,
      organisationArn: organisationDetailsResponse?.value?.value[0]?.casa_arn,
      rootFormCode: '',
      formCode: '',
      formData: values
    };

    try {
      // Send request to Submission service
      const response = await await damReportService.submitCreateDampReportApplication(dampApplicationPayLoad);

      setSubmitting(false);
      // If request failed return error to the user
      if (!response || !response.wasSuccessful) {
        return errorAlert;
      }
      // If successful return success to the user
      return {
        as: 'success',
        title: 'DAMP Report submitted',
        message: 'We have received your DAMP report'
      };
    }
    catch (error) {
      return errorAlert;
    }
  };

  const breadcrumbs: IAuBreadcrumbItem[] = [
    { link: '/', text: 'myCASA' },
    { text: 'DAMP Report' }
  ];

  return (
    <>
      <MetaTags>
        <title>DAMP report</title>
      </MetaTags>
      <AuBreadcrumbs label='breadcrumbs' items={breadcrumbs} />
      <FormContextProvider
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        steps={STEPS}>
        <FormContext.Consumer>
          {({ currentStepIndex, steps, currentStepProps }: IFormContextType) => (
            <Form name='DAMP Report'>
              <AuFormItemWrapper>
                <FormAlert htmlFor='DAMP Report' />
              </AuFormItemWrapper>
              <ExitModal exitModalParams={exitModalParameters} />
              {steps[currentStepIndex].render(currentStepProps)}
            </Form>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </>
  );
};

export default DampReportApplication;